import { Checkbox, Input, message } from "antd";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Router from "../";
import logoMobile from "../../../src/images/SurfingCre1.png";
import logo from "../../../src/images/SurfingCre2.png";
import { useGoogleLogin } from "@react-oauth/google";
import { useLanguageStore } from "../../stores/languageStore";

const SIGN_IN_TYPES = [
  { key: "brand", label: "브랜드", labelEn: "Brand" },
  { key: "agency", label: "에이전시", labelEn: "Agency" },
  { key: "influencer", label: "크리에이터", labelEn: "Creator" },
];

export const SignIn = () => {
  // ✅ antd message 로컬 인스턴스
  const [messageApi, contextHolder] = message.useMessage();
  const history = useHistory();
  const lang = useLanguageStore((state) => state.lang);
  const location = useLocation();

  const getSignInTypeFromSearch = (search) => {
    const type = new URLSearchParams(search).get("type");
    return ["brand", "agency", "influencer"].includes(type) ? type : "brand";
  };

  useEffect(() => {
    if (localStorage.getItem("autoLoginEnabled") === "true") {
      Router.Api.autoLogin(history);
    }
  }, [history]);

  const signInType = getSignInTypeFromSearch(location.search);
  const [authType, setAuthType] = useState("login");
  const [loginFields, setLoginFields] = useState({
    email: "",
    password: "",
    autoLogin: false,
  });
  const [findAuthFields, setFindAuthFields] = useState({
    email: "",
  });

  const handleLoginChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setLoginFields((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleFindAuthChange = (e) => {
    const { name, value } = e.target;
    setFindAuthFields((prev) => ({ ...prev, [name]: value }));
  };

  const validateAndCheckEmail = async (email) => {
    if (!email) {
      messageApi.warning("이메일을 입력해주세요.");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      messageApi.error("올바른 이메일 형식을 입력해주세요.");
      return false;
    }
    return true;
  };

  const handleResetPassword = async () => {
    if (!(await validateAndCheckEmail(findAuthFields.email))) return;
    const success = await Router.Api.resetPassword({
      email: findAuthFields.email,
    });
    if (success) {
      setFindAuthFields({ email: "" });
      messageApi.success("비밀번호 재설정 메일을 보냈습니다.");
    }
  };

  const onLoginSubmit = async (e) => {
    e.preventDefault();
    const loginResult = await Router.Api.login({
      id: loginFields.email,
      appKey: loginFields.password,
      platform: "own",
      autoLoginEnabled: loginFields.autoLogin,
    });

    // 로그인 실패
    if (loginResult?.error) {
      messageApi.error(loginResult.error.text || "로그인에 실패했습니다.");
      return;
    }

    // 로그인 성공
    if (loginResult?.result) {
      const userType = loginResult.result.user_info?.user_type;

      if (!userType) return;

      if (signInType === "brand" && userType !== "brand") {
        localStorage.clear();
        const accountType = userType === "agency" ? "에이전시" : "크리에이터";
        messageApi.error(`${accountType} 계정입니다.`);
        return;
      }

      if (signInType === "agency" && userType !== "agency") {
        localStorage.clear();
        const accountType = userType === "brand" ? "브랜드" : "크리에이터";
        messageApi.error(`${accountType} 계정입니다.`);
        return;
      }

      if (signInType === "influencer" && userType !== "influencer") {
        localStorage.clear();
        const accountType = userType === "brand" ? "브랜드" : "에이전시";
        messageApi.error(`${accountType} 계정입니다.`);
        return;
      }

      history.push("/home");
      return;
    }
  };

  const handleSignInTypeChange = (type) => {
    history.replace(`/signin?type=${type}`);
    setLoginFields({
      email: "",
      password: "",
      autoLogin: false,
    });
    setFindAuthFields({
      email: "",
    });
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      // 구글 사용자 정보 가져오기
      const userInfoResponse = await fetch(
        "https://www.googleapis.com/oauth2/v3/userinfo",
        {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        }
      );
      const googleUser = await userInfoResponse.json();

      const googleEmail = googleUser.email;
      const googleOAuthId = googleUser.sub; // oauth_id
      const firstName = googleUser.given_name || "";
      const lastName = googleUser.family_name || "";

      const loginResult = await Router.Api.login({
        id: googleEmail,
        appKey: `geumbada${googleOAuthId}`,
        platform: "google",
        autoLoginEnabled: false,
      });
      if (loginResult.result) {
        history.push("/home");
        return;
      }

      if (loginResult.error) {
        if (loginResult.error.text === "없는 계정 정보입니다.") {
          // 회원가입 이동
          sessionStorage.setItem(
            "socialSignup",
            JSON.stringify({
              platform: "google",
              oauth_id: googleOAuthId,
              email: googleEmail,
              first_name: firstName,
              last_name: lastName,
            })
          );
          history.push("/signup?social=google");
          return;
        } else {
          messageApi.error(loginResult.error.text);
          return;
        }
      }
    },
    onError: (error) => {
      messageApi.error("구글 로그인 인증에 실패했습니다. 다시 시도해 주세요.");
    },
  });

  return (
    <div className="auth flex flex-col md:flex-row">
      {contextHolder}

      <div className="absolute top-4 left-4 z-10">
        <div className="w-14 h-14 flex items-center justify-center">
          <Router.LanguageTranslator iconColor="black" />
        </div>
      </div>

      {authType === "login" && (
        <>
          <div className="login_box w-full md:w-[45%] md:shadow-[8px_0px_8px_0_rgba(50,42,42,0.7)]">
            <img
              src={logoMobile}
              className="block md:hidden mb-12 mx-auto w-1/2"
              alt="로고"
            />
            <img src={logo} className="logo hidden md:block" alt="로고" />
            <h1>{lang === "en" ? "Login" : "로그인"}</h1>

            {/* 사용자 타입 선택 */}
            <div className="w-full md:px-20 md:max-w-[500px] mb-6">
              <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                {SIGN_IN_TYPES.map((type) => {
                  const isActive = signInType === type.key;

                  return (
                    <button
                      key={type.key}
                      type="button"
                      onClick={() => handleSignInTypeChange(type.key)}
                      className={`flex-1 py-3 px-2 md:px-3 lg:px-4 rounded-md text-xl shadow-sm font-medium transition-all duration-200 whitespace-nowrap
                        ${
                          isActive
                            ? "bg-white text-[#001d35] font-semibold"
                            : "text-gray-600"
                        }`}
                    >
                      {lang === "en" ? type.labelEn : type.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <form
              onSubmit={onLoginSubmit}
              className="signup_form w-full md:px-20 md:max-w-[500px]"
            >
              <p>{lang === "en" ? "Email" : "이메일"}</p>
              <Input
                type="text"
                size="large"
                name="email"
                value={loginFields.email}
                onChange={handleLoginChange}
                placeholder="이메일을 입력해주세요"
                autoComplete="username"
                required
              />
              <p>{lang === "en" ? "Password" : "비밀번호"}</p>
              <Input
                type="password"
                size="large"
                name="password"
                value={loginFields.password}
                onChange={handleLoginChange}
                placeholder="비밀번호를 입력하세요"
                autoComplete="current-password"
                required
              />
              <div>
                <Checkbox
                  name="autoLogin"
                  checked={loginFields.autoLogin}
                  onChange={(e) => {
                    handleLoginChange({
                      target: {
                        name: e.target.name,
                        type: "checkbox",
                        checked: e.target.checked,
                      },
                    });
                  }}
                  className="mt-5"
                >
                  자동 로그인
                </Checkbox>
              </div>
              <button type="submit" className="btn">
                {lang === "en" ? "Login" : "로그인"}
              </button>

              <div className="flex items-center justify-center gap-2">
                <button
                  type="button"
                  className="find_auth_btn text-[#1890ff] cursor-pointer hover:text-[#1890ff]/70"
                  onClick={() => {
                    history.push(`/signup?type=${signInType}`);
                  }}
                >
                  {lang === "ko"
                    ? `${
                        SIGN_IN_TYPES.find((type) => type.key === signInType)
                          ?.label
                      } 회원가입`
                    : `${
                        signInType === "brand"
                          ? "Brand"
                          : signInType === "agency"
                            ? "Agency"
                            : "Creator"
                      } Membership Registration`}
                </button>
                <span className="text-gray-300">|</span>
                <button
                  type="button"
                  className="find_auth_btn text-[#1890ff] cursor-pointer hover:text-[#1890ff]/70"
                  onClick={() => {
                    setAuthType("findauth");
                    setLoginFields({
                      email: "",
                      password: "",
                      autoLogin: false,
                    });
                  }}
                >
                  비밀번호 찾기
                </button>
              </div>

              <div
                className={`mt-8 ${
                  signInType === "influencer" ? "visible" : "invisible"
                }`}
              >
                <div className="text-center text-[#6d6d6d] mb-6">
                  SNS 계정으로 로그인 또는 회원가입하기
                </div>
                <Router.SocialLogin onClick={() => googleLogin()} />
              </div>
            </form>
          </div>
          <div className="intro hidden md:block flex-1"></div>
        </>
      )}

      {authType === "findauth" && (
        <>
          <div className="login_box w-full md:w-[45%] md:shadow-[8px_0px_8px_0_rgba(50,42,42,0.7)]">
            <img
              src={logoMobile}
              className="block md:hidden mb-12 mx-auto w-1/2"
              alt="로고"
            />
            <img src={logo} className="logo hidden md:block" alt="로고" />
            <h1>비밀번호 찾기</h1>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleResetPassword();
              }}
              className="signup_form w-full md:px-20 md:max-w-[500px]"
            >
              <p>이메일</p>
              <Input
                size="large"
                type="email"
                name="email"
                value={findAuthFields.email}
                onChange={handleFindAuthChange}
                placeholder="가입하신 이메일을 입력해주세요"
                autoComplete="email"
                required
              />
              <span className="text-xl text-gray-700 mt-2">
                메일이 오지 않은 경우, 스팸함을 확인해주세요.
              </span>

              <button type="submit" className="btn w-full py-2.5 md:text-2xl">
                재설정 메일 받기
              </button>

              <button
                type="button"
                className="signup_btn w-full py-2.5"
                onClick={() => {
                  setAuthType("login");
                  setFindAuthFields({ email: "" });
                }}
              >
                로그인 페이지로 돌아가기
              </button>
            </form>
          </div>
          <div className="intro hidden md:block flex-1"></div>
        </>
      )}
    </div>
  );
};
